
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import TestCard from "./TestCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Box } from "lucide-react";

interface Test {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface TestSelectorProps {
  puttingTests: Test[];
  chippingTests: Test[];
  wedgingTests: Test[];
  bunkerTests: Test[];
  ironTests: Test[];
  fairwayWoodTests: Test[];
  driverTests: Test[];
  onSelectTest: (testId: string) => void;
}

const TestSelector: React.FC<TestSelectorProps> = ({ 
  puttingTests, 
  chippingTests, 
  wedgingTests,
  bunkerTests,
  ironTests,
  fairwayWoodTests,
  driverTests,
  onSelectTest 
}) => {
  const { t, i18n } = useTranslation("training");
  
  // Force re-render when language changes
  React.useEffect(() => {
    // This effect will run whenever the language changes
  }, [i18n.language]);
  
  // Fonction pour rendre un test card avec traductions
  const renderTestCard = (test: Test, category: string) => {
    const translationKey = `level2.testData.${category}.${test.id}`;
    const translatedTitle = t(`${translationKey}.title`, { defaultValue: test.title });
    const translatedDescription = t(`${translationKey}.description`, { defaultValue: test.description });
    
    return (
      <TestCard
        key={test.id}
        id={test.id}
        title={translatedTitle}
        description={translatedDescription}
        icon={test.icon}
        onSelectTest={onSelectTest}
      />
    );
  };
  
  // Fonction pour rendre une section de tests
  const renderTestSection = (title: string, icon: string, description: string, tests: Test[], category: string) => {
    return (
      <Card className="border-2 border-golf-600 mb-8">
        <CardHeader className="bg-golf-50">
          <div className="flex items-center gap-2">
            <Box className="h-6 w-6 text-golf-700" />
            <CardTitle className="text-xl text-golf-800">{title}</CardTitle>
          </div>
          <CardDescription className="text-golf-600">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map(test => renderTestCard(test, category))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-12"
    >
      {renderTestSection(
        t("level2.exerciseTitles.putting"), 
        "🎯",
        t("level2.exerciseDescriptions.putting"),
        puttingTests,
        "putting"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.chipping"),
        "⛳",
        t("level2.exerciseDescriptions.chipping"),
        chippingTests,
        "chipping"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.wedging"),
        "🏌️",
        t("level2.exerciseDescriptions.wedging"),
        wedgingTests,
        "wedging"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.bunker"),
        "🏖️",
        t("level2.exerciseDescriptions.bunker"),
        bunkerTests,
        "bunker"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.ironAndWood"),
        "🎯",
        t("level2.exerciseDescriptions.ironAndWood"),
        ironTests,
        "iron"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.ironAndWood"),
        "🎯",
        t("level2.exerciseDescriptions.ironAndWood"),
        fairwayWoodTests,
        "fairwayWood"
      )}
      {renderTestSection(
        t("level2.exerciseTitles.driver"),
        "🏌️",
        t("level2.exerciseDescriptions.driver"),
        driverTests,
        "driver"
      )}
    </motion.div>
  );
};

export default TestSelector;
